/**
 * サンドボックス制御プロトコルのスキーマ（SSOT）
 *
 * デスクトップ ⇔ 中継 ⇔ 実行環境内エージェントの間でやり取りする制御メッセージの
 * 単一定義。agent-core / desktop-app / infra / sandbox-agent の 4 者がここを参照する。
 *
 * **エージェントはイメージへ焼き込まれ、デスクトップとは独立に更新されうる。**
 * したがって接続時に取り決めの版を交換し、不一致ならリースを失敗として扱う。
 * 版が合わないまま通信を続けると、片側が解釈できない要求を送り続けることになる。
 */
import { z } from 'zod';
/**
 * 取り決めの版。
 *
 * メッセージの形を変えたら上げる。デスクトップとエージェントで一致しない場合、
 * リースは `failed` となり利用者には再試行ではなくアプリ更新が案内される。
 *
 * **基準は中継が持つ版である。** 中継と実行環境のイメージは同じ配備で更新されるが、
 * デスクトップは別に配布される。中継がデスクトップとエージェントの双方を自分の版と
 * 照合することで、デスクトップとイメージのずれを検出する。
 *
 * 履歴:
 * - 1: 初版
 * - 2: `file-read` に `encoding`（base64 読み取り）、`file-write` に `append`（追記）を追加。
 *      版 1 のエージェントはこれらを無視し、追記を上書きで、base64 を UTF-8 で
 *      **成功として**返してしまう
 */
export const SANDBOX_PROTOCOL_VERSION = 2;
/**
 * 制御メッセージへ直接載せられる本文の上限（バイト）。
 *
 * ## 効いてくるのは「メッセージ」ではなく「フレーム」の上限
 *
 * API Gateway の WebSocket には 2 つの上限がある。**メッセージは 128KiB、
 * フレームは 32KiB。** 128KiB まで送れるのは、送信側が自分で 32KiB 以下へ
 * 分割した場合に限る。デスクトップも実行環境も組み込みの WebSocket を
 * 1 回の `send` で 1 フレームとして送るため、**実際の上限は 32KiB である**。
 *
 * 超えたフレームは中継へ届かない。届かなかったことは誰にも分からず、応答が
 * 来ないまま待たされる形でだけ現れる。128KiB を前提に置いていたため、
 * **32KiB から 64KiB の間に収まる応答だけが失われていた**（それ未満はそのまま
 * 通り、それ以上は参照へ切り替わって通る）。44KB の JPEG が base64 で 58KB に
 * なり、この帯へ落ちて読めなかった。
 *
 * これを超える本文は**送る前に**一時領域へ退避し、参照だけを載せる。
 * **判定は送信側で行う。** 中継に届いてから判定しても手遅れである。
 *
 * 32KiB そのものではなく余裕を見た値にする。封筒（相関付け・リース識別子・
 * 種別など）と JSON の追加分がこの上に乗るため。
 */
export const MAX_INLINE_BODY_BYTES = 24 * 1024;
/**
 * 1 回の退避で計上しうるバイト数の上限。
 *
 * **参照に書かれた大きさは自己申告である。** 中継は S3 の実体を測らず
 * （測るには往復が増える）、`StagingRef.bytes` をそのまま転送量へ加算する。
 * 加算先は利用者の行と**全体の行**の両方であり、全体の上限（既定 1TB／月）に
 * 達すると、その月は**全利用者**のクラウド確保が拒否される。使用量の行は TTL を
 * 持たないため自然回復せず、上限を上げるか記録を直すまで戻らない。
 *
 * サンドボックス内で走るのは利用者由来のコードであり、エージェントが乗っ取られる
 * ことは脅威として見込んでいる。申告 1 通で全員を止められては困るため、
 * **計上はここで頭打ちにする**（実体がこれを超えていても、過少に数える側へ倒す）。
 *
 * 値は 1 回の一括転送の上限（クラウドは 128MiB）に、base64（約 1.34 倍）と
 * JSON の追加分を見込んだもの。正当な退避がこれを超えることはない
 */
export const MAX_STAGED_ACCOUNTED_BYTES = 256 * 1024 * 1024;
/**
 * `file-read` が 1 回で返すテキストの上限（バイト）。
 *
 * **ローカル実行先と同じ値にする。** ローカルは `cat` の出力を
 * `SandboxConfig.MAX_OUTPUT_SIZE`（1MB）で頭打ちにし、超えた分を切り詰めて返す。
 * 実行環境側に同じ上限が無いと、AI が自分で作った数百 MB のログを読もうとした
 * ときにエージェントが文字列化の上限（`ERR_STRING_TOO_LONG`）か枯渇で落ちる。
 * **エージェントは PID 1 である**ため、その割り当てに乗っている作業がすべて道連れになる。
 */
export const MAX_FILE_READ_BYTES = 1024 * 1024;
/**
 * `file-list` が 1 回で返す項目数の上限。
 *
 * **両側が同じ値を見る。** エージェントはここで打ち切り、ホスト側は「版の古い
 * エージェントが `truncated` を載せてこなかったとき、項目数がこの値に達して
 * いれば打ち切られている」と読む。別々に持つと、ホストが広い値を見ている間だけ
 * 打ち切りが「全部ある」と読まれる。
 *
 * ローカル実行先は `ls -la` の出力量（`MAX_COMMAND_OUTPUT_BYTES`、1 行 60〜80
 * バイトなので実効 1 万件強）で頭打ちになる。値は揃っていないが、**どちらも
 * 打ち切りを `truncated` で申告する**ため、呼び出し側の扱いは同じになる。
 */
export const MAX_LISTED_ENTRIES = 1_000;
/**
 * コマンド実行が 1 回で返す出力の上限（バイト）。
 *
 * **ローカル実行先と同じ値にする。** ローカルは `SandboxConfig.MAX_OUTPUT_SIZE`
 * （1MB）で stdout / stderr を頭打ちにし、超えた分を切り詰めて `truncated` を
 * 立てる。実行環境側の上限をこれより広くすると、同じ `cat` が実行先によって
 * 1MB と数 MB に分かれる。広いほうは中継に載らないため一時領域へ退避され、
 * **利用者の月間転送量に満額が計上される**うえ、その全量がモデルへ渡る。
 *
 * 値の出どころを 1 つにできないのは、`SandboxConfig` を持つ `@kyumo/agent-core` を
 * 実行環境内エージェントが取り込めない（イメージへ焼き込むため）からである。
 * 片方だけを動かさないよう、両方から参照されるここに理由ごと置く。
 */
export const MAX_COMMAND_OUTPUT_BYTES = 1024 * 1024;
/**
 * `file-read` を base64 で行うときの**元ファイル**の上限（バイト）。
 *
 * base64 は約 33% 膨らむため、返す側の上限（`MAX_FILE_READ_BYTES`）の 3/4 とする。
 * ローカル実行先（`ContainerFileService.readFileBase64`）と同じ算出であり、
 * 同じ約 750KB になる。
 *
 * **こちらは切り詰めず失敗として返す。** 画像の読み取りは base64 を途中で切ると
 * 壊れた画像になるだけで、呼び出し側（`sandbox__readFile`）も画像経路では
 * 切り詰めを行わない。ローカルと同じく「大きすぎる」と伝えるほうが手が打てる
 */
export const MAX_FILE_READ_BASE64_SOURCE_BYTES = Math.floor((MAX_FILE_READ_BYTES * 3) / 4);
/**
 * 生存通知 1 回で送れる置き場（スコープ）の件数。
 *
 * 受け側は受け取った数だけ書き込みを出すため、上限を設けている。
 * **送る側も同じ値を見る。** 別々に持つと、超えた通知が 400 で弾かれるだけになり、
 * 回収側から見れば「アプリが落ちた」と区別が付かない。長く使った端末ほど
 * 置き場は増えるため、これは静かに作業ファイルを失う経路になる。
 * 送る側はこの単位で分けて送る。
 */
export const MAX_LIVENESS_SCOPE_IDS = 200;
/** 版が両立するか。現状は同一版のみを許す */
export function isProtocolCompatible(peerVersion) {
    return peerVersion === SANDBOX_PROTOCOL_VERSION;
}
// ========================================
// 要求種別
// ========================================
/**
 * 要求種別。制御メッセージの判別子となる。
 *
 * ポート（`SandboxProvider`）の操作と 1 対 1 に対応する。
 */
export const SandboxRequestKindSchema = z.enum([
    'exec-command',
    'exec-code',
    'file-read',
    'file-write',
    'file-list',
    'file-delete',
    'file-stat',
    'mkdir',
    'export-path',
    'import-path',
]);
// ========================================
// 要求の中身（種別ごと）
// ========================================
/**
 * `file-read` の中身。
 *
 * **`as` で読まない。** 型だけで済ませると、片側が項目を足しても反対側は
 * 黙って無視し、成功として誤った結果を返す（`encoding` で実際に起きた）。
 * スキーマで受けることで、足した項目の扱いを両側が同じ定義から引く
 */
export const SandboxFileReadPayloadSchema = z.object({
    path: z.string().min(1),
    /** 未指定は UTF-8 のテキスト。画像などの二進内容は base64 で運ぶ */
    encoding: z.enum(['utf-8', 'base64']).optional(),
});
/**
 * パスだけを取る操作（`file-list` / `file-stat` / `mkdir`）の中身。
 *
 * **`as` で読まない。** 項目が欠けた要求（項目名の食い違い、退避した本文の
 * 取り違え）では `undefined` が `fs.readdir`／`fs.mkdir` へ渡り、検証の失敗では
 * なく `ERR_INVALID_ARG_TYPE` という実装都合の文言で返る。空文字も同様に
 * 弾く（兄弟の操作はいずれも `min(1)` で弾いている）
 */
export const SandboxFilePathPayloadSchema = z.object({
    path: z.string().min(1),
});
/** `file-write` の中身 */
export const SandboxFileWritePayloadSchema = z.object({
    path: z.string().min(1),
    /**
     * 書き込む中身。**空文字は許すが、項目そのものの欠落は許さない。**
     *
     * 既定値を与えると、項目が欠けた要求（版のずれ・項目名の変更・退避本文の
     * 取り違え）が素通りして `writeFile(path, '')` になり、**既存のファイルを
     * 空にしたうえで成功を返す**。書き手が「空にする」と言ったのか、
     * ただ言い忘れたのかを取り違えてよい操作ではない。
     * ホスト側（`cloud-sandbox-provider`）は上書き・追記のどちらでも
     * 必ずこの項目を送る
     */
    content: z.string(),
    /** 真なら末尾へ追記する。未指定は上書き */
    append: z.boolean().default(false),
});
/** `file-delete` の中身 */
export const SandboxFileDeletePayloadSchema = z.object({
    path: z.string().min(1),
    /**
     * 真ならディレクトリを配下ごと消す。未指定はファイルのみ。
     *
     * **既定を偽にしておく。** 承認不要の削除ツールがこの操作を使うため、
     * 既定で再帰的に消すと、作業ディレクトリを渡されただけで永続化された領域が
     * 丸ごと失われる。再帰削除を要るのは転送失敗時の後片付けだけで、そこは
     * 自分が書いた宛先しか渡さない
     */
    recursive: z.boolean().default(false),
});
/**
 * 取り出しの上限。
 *
 * **`as` で読まない。** これは実行環境側の記憶量を縛る唯一の値である。項目名が
 * ひとつでも食い違うと、比較の相手が `undefined` になってその上限だけが黙って
 * 外れ、木を丸ごと base64 で記憶へ積み上げてコンテナごと落ちる。落ちるのは
 * この要求だけでなく、同じ実行環境で動いている他のタスクも巻き添えになる
 */
export const SandboxTransferLimitsSchema = z.object({
    maxFileBytes: z.number().int().positive(),
    maxTotalBytes: z.number().int().positive(),
    maxEntries: z.number().int().positive(),
});
/** `export-path` の中身 */
export const SandboxExportPathPayloadSchema = z.object({
    path: z.string().min(1),
    /** 未指定は内部利用（上限判定なし）。渡すなら 3 項目すべてが要る */
    limits: SandboxTransferLimitsSchema.optional(),
});
/** `exec-command` の中身 */
export const SandboxExecCommandPayloadSchema = z.object({
    /**
     * 実行する語の並び。
     *
     * **空を許さない。** 既定値で補うと `bash -c ''` が走り、終了コード 0 の
     * 「成功したが出力が無いコマンド」として返る。ローカルの実行先では
     * docker exec が空の引数列を拒むため、同じ壊れた要求が実行先によって
     * 別の結果になる
     */
    command: z.array(z.string()).min(1),
});
/**
 * `exec-code` の中身。
 *
 * **`exec-command` と同じ理由でスキーマで受ける。** `as string` で読むと、
 * 項目が欠けた・型の違う要求が既定値（`''` / `'bash'`）で補われ、`bash -c ''` が
 * 終了コード 0 で返る。壊れた要求が「実行したが出力が無かった」として成功で
 * 返り、AI は自分の書いたコードが走ったものとして先へ進む。
 * 文字列以外が来た場合はさらに悪く、`bash` では本文がそのまま台本になるため
 * `[object Object]` をシェルが実行することになる。
 */
export const SandboxExecCodePayloadSchema = z.object({
    /** 空を許さない（空の台本は成功として返ってしまう） */
    code: z.string().min(1),
    /** 解釈系。未知の値は台本をそのままシェルへ渡す扱いになるため、ここで閉じる */
    runtime: z.enum(['bash', 'python', 'node']).default('bash'),
    /**
     * 呼び出し元が解決したインタプリタ。
     * 無視すると、同梱の Python でやり直すつもりの再実行が同じ解釈系を走らせる
     */
    pythonPath: z.string().min(1).optional(),
});
// ========================================
// 本体
// ========================================
/**
 * 一時領域へ退避した内容への参照。
 *
 * `sha256` は同一内容の再転送を避けるための比較に使う（要件 5.6）。
 * `bytes` は転送量の計上に使う。
 */
export const StagingRefSchema = z.object({
    key: z.string().min(1),
    bytes: z.number().int().nonnegative(),
    sha256: z.string().min(1),
});
/**
 * `import-path` の中身。
 *
 * **兄弟と同じくスキーマで受ける。** `as string` で読むと空文字が素通りし、
 * 展開先の親を取る `path.dirname('')` が `'.'` を返す。運ばれてきたファイルが
 * 実行環境の作業ディレクトリ（接続点のマウント点そのもの）へ展開され、スコープの
 * 作業領域の外へ出る。相対パスも同じ経路で外へ出るため、絶対パスだけを受ける。
 *
 * 項目が欠けた場合も同様で、`path.dirname(undefined)` の `ERR_INVALID_ARG_TYPE` は
 * 「サンドボックスが使えない」へ写されてしまい、検証の失敗として届かない。
 *
 * **`StagingRefSchema` より後に置く。** 前方参照はできない。
 */
export const SandboxImportPathPayloadSchema = z.object({
    path: z.string().min(1).startsWith('/'),
    /** 退避した実体への参照。取得先そのものは中継が添える許可から取る */
    ref: StagingRefSchema,
});
/**
 * 制御メッセージの本体。
 *
 * **上限を超える内容は必ず参照の形になる。** 中継が置き換えるため、
 * 受け手は常に両方の形を扱えなければならない。
 */
export const SandboxResultBodySchema = z.discriminatedUnion('form', [
    z.object({ form: z.literal('inline'), value: z.unknown() }),
    z.object({ form: z.literal('staged'), ref: StagingRefSchema }),
]);
// ========================================
// 封筒
// ========================================
/** デスクトップ → エージェントの要求 */
export const SandboxEnvelopeSchema = z.object({
    /** 要求と応答を対応付ける。順序保証は行わないため必須 */
    correlationId: z.string().min(1),
    /** 帰属するリース。中継はこれと送信元の一致を検証する */
    leaseId: z.string().min(1),
    kind: SandboxRequestKindSchema,
    body: SandboxResultBodySchema,
});
/**
 * 一時領域への期限付きの許可。
 *
 * **エージェントは恒久的な保管庫の権限を持たない。** タスクロールに権限が無いため、
 * 中継が所有者を確かめたうえで発行したこの許可だけが到達手段になる。
 *
 * `upload` は要求を中継する時点で必ず添える。結果の大きさは実行してみるまで
 * 分からず、超過が判明してから許可を取りに行く経路を作ると往復が増えるうえ、
 * その往復が失敗したときに結果を返す手段が無くなる。
 */
export const SandboxStagingGrantSchema = z.object({
    /**
     * 結果を書き出す先。鍵は中継が切る。
     *
     * 発行に失敗しても要求そのものは通す（上限を超える結果になるとは限らない）ため、
     * 欠けうる。欠けたまま超過した場合は失敗として返し、黙って切り詰めない。
     */
    upload: z.object({ key: z.string().min(1), url: z.string().min(1) }).optional(),
    /** 要求本文が参照の形で届いたときの取得先 */
    downloadUrl: z.string().min(1).optional(),
});
/**
 * 中継がエージェントへ渡す封筒。
 *
 * デスクトップが送る `SandboxEnvelope` に、中継が一時領域の許可を添えたもの。
 * **デスクトップ側に許可を作らせない。** 送信者が自分で書ける値にすると、
 * 所有者検証を通していない鍵をエージェントへ渡せてしまう。
 */
export const SandboxForwardedEnvelopeSchema = SandboxEnvelopeSchema.extend({
    staging: SandboxStagingGrantSchema.optional(),
});
/** エージェント → デスクトップの応答・報告 */
export const SandboxAgentMessageSchema = z.object({
    correlationId: z.string().min(1),
    leaseId: z.string().min(1),
    /**
     * 実行中コマンド数の実測値。
     *
     * 中継はこの値で永続化された値を**上書きする**（加減算しない）。
     * 応答とハートビートの双方に載せることで、取りこぼしがあっても次の報告で自己修復する。
     */
    runningCommandCount: z.number().int().nonnegative(),
    body: SandboxResultBodySchema,
});
// ========================================
// ハンドシェイク
// ========================================
/** 接続直後にエージェントが送る自己紹介 */
export const SandboxHandshakeSchema = z.object({
    protocolVersion: z.number().int().positive(),
    leaseId: z.string().min(1),
    scopeId: z.string().min(1),
});
/**
 * 版の不一致の通知。中継が自分の版と合わない相手へ送る。
 *
 * 受け取った側は割り当てを失敗として扱う（デスクトップは利用者へアプリ更新を
 * 案内し、エージェントは終了する）。
 */
export const SandboxProtocolMismatchSchema = z.object({
    type: z.literal('protocol-mismatch'),
    /** 中継が要求する版 */
    expectedVersion: z.number().int().positive(),
    /** 相手が名乗った版 */
    peerVersion: z.number().int().positive(),
});
/**
 * 中継できなかった要求の通知。中継がデスクトップへ返す。
 *
 * **WebSocket のルートは統合の応答を返さない。** 中継が 400／403／500 を返しても
 * 呼び出し側には何も届かず、待ち中の要求は既定の期限（5 分超）まで残ってから
 * 「応答が途絶えた」として畳まれる。接続の張り直しの最中に来た要求のような
 * 日常的な失敗が、そのたびに数分の沈黙になる。
 *
 * そこで中継は、運べなかったことを相関付き**でその場で返す**。受け取った側は
 * 対応する要求を失敗として畳む。
 */
export const SandboxRequestFailedSchema = z.object({
    type: z.literal('request-failed'),
    /** 運べなかった要求の相関付け */
    correlationId: z.string().min(1),
    /** 宛先の照合に使う（同じ利用者の他のスコープが受け取っても無視できるように） */
    leaseId: z.string().min(1),
    /** 失敗の区分。利用者向けの文言は受け取った側が組み立てる */
    reason: z.enum(['denied', 'internal']),
    /**
     * 遮断の内訳。**「割り当てへ繋がっているエージェントが居ない」ときだけ載る。**
     *
     * `denied` は転送量の上限・所有者検証の失敗でも返るが、それらは割り当てを
     * 取り直しても解消しない。区別せずに取り直すと、枠を使い切った利用者の
     * 大きな書き込みが、**再確保と一時領域への再退避を伴って**もう一度同じ
     * 拒否に当たる（枠をさらに削る）。受け取った側はこの内訳が載っているときだけ
     * 割り当てを取り直して送り直す。
     *
     * **任意項目のままにする。** 載せない中継（配備前）と載せる中継が混在しても、
     * 受け取った側は「取り直さない」（＝従来どおり）へ倒れるだけで壊れない
     */
    cause: z.literal('no-agent').optional(),
});
/** 準備完了の通知。エージェントはスコープの準備を終えてからこれを送る */
export const SandboxReadySchema = z.object({
    leaseId: z.string().min(1),
    scopeId: z.string().min(1),
    /** Python 仮想環境の用意に成功したか。失敗しても縮退して継続する */
    venvReady: z.boolean(),
});
//# sourceMappingURL=sandbox-protocol-schemas.js.map